import { useState, useCallback } from 'react';

const MAX_HISTORY_SIZE = 50;

export function useUndoRedo(initialState = null) {
  const [history, setHistory] = useState({
    past: [],
    present: initialState,
    future: []
  });

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  // Push a new state to history
  const pushState = useCallback((newState) => {
    setHistory(prev => {
      // Skip if state hasn't changed
      if (JSON.stringify(prev.present) === JSON.stringify(newState)) {
        return prev;
      }

      // Don't add null/undefined initial state to past
      const newPast = prev.present === null
        ? prev.past
        : [...prev.past.slice(-(MAX_HISTORY_SIZE - 1)), JSON.parse(JSON.stringify(prev.present))];

      return {
        past: newPast,
        present: JSON.parse(JSON.stringify(newState)),
        future: [] // Clear redo stack on new change
      };
    });
  }, []);

  // Undo - go back one state
  const undo = useCallback(() => {
    setHistory(prev => {
      if (prev.past.length === 0) return prev;

      const previous = prev.past[prev.past.length - 1];
      return {
        past: prev.past.slice(0, -1),
        present: previous,
        future: [JSON.parse(JSON.stringify(prev.present)), ...prev.future]
      };
    });
  }, []);

  // Redo - go forward one state
  const redo = useCallback(() => {
    setHistory(prev => {
      if (prev.future.length === 0) return prev;

      const next = prev.future[0];
      return {
        past: [...prev.past, JSON.parse(JSON.stringify(prev.present))],
        present: next,
        future: prev.future.slice(1)
      };
    });
  }, []);

  // Reset history with new state (e.g., when loading a new page)
  const reset = useCallback((newState) => {
    setHistory({
      past: [],
      present: newState !== undefined ? JSON.parse(JSON.stringify(newState)) : null,
      future: []
    });
  }, []);

  // Get current state
  const state = history.present;

  return {
    state,
    pushState,
    undo,
    redo,
    reset,
    canUndo,
    canRedo
  };
}

export default useUndoRedo;
